// B-77 — the client's day in progress, followed above the panels.
//
// "Procesar el día" used to be followed by the `NuevoCierre` that sent it. Since B-70 the panel is
// remounted when a day is opened (`?fecha=`) and again on the way back, so the chip and its poll
// were gone the moment the operator looked at anything. This lives in `ClienteCierresProvider`,
// which frames every Cierres screen, and every `NuevoCierre` reads it.
//
// It also answers for a run nobody sent from here. The backend's lock is per client, so
// `getCierreDailyProgress` says `running` whatever date it is asked about, and `runningDate` says
// which one (B-77's backend half). So: one question when the client changes or a panel asks
// (`revisar`), and a poll only while a run is live. A backend without `runningDate` falls back to
// the date this browser sent, remembered per business and client so a reload still knows it.
//
// A client with no daily documents (Davivienda) costs that one question and is never polled.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getCierreDailyProgress, listCierreRunNotifications } from "../../../ApiCalls/mantisBusiness";
import { avanceAlEnviar, clavesDiarias, fechaPorDefecto, resumenDeAvance } from "../../../utils/cierreDiaria";
import { getFromStorage, setInStorage } from "../../../utils/storage";
import { ESPERAS_DE_ARRANQUE, INTERVALO_AVANCE_MS } from "./useConciliacionDia";

const claveDeFecha = (mantisBusinessId, clientCode) => `cierres_dia_en_curso_${mantisBusinessId}_${clientCode}`;

// A notification counts as this run's only if written after the run was first seen, less a margin
// for the two clocks (browser and server) not agreeing.
const MARGEN_DE_RELOJ_MS = 2 * 60 * 1000;

// How the run this browser followed ended, as the backend wrote it for the person who ran it
// (B-77): the counters alone cannot tell a run that stopped from a day already read before it.
// Only the runner has that notification; anyone else gets `null` and the counters stand.
async function resultadoDelProceso(token, clientCode, fecha, desde) {
  const response = await Promise.resolve()
    .then(() => listCierreRunNotifications(token))
    .catch(() => null);
  if (!response?.success) return null;
  return (
    (response.data?.notifications || []).find(
      (n) => n.clientCode === clientCode && n.businessDate === fecha && new Date(n.dateCreated).getTime() >= desde - MARGEN_DE_RELOJ_MS
    ) || null
  );
}

export const DIA_INACTIVO = {
  corriendo: false,
  terminado: false,
  fallo: null,
  fecha: "",
  avance: resumenDeAvance(null),
  fin: 0,
  diario: null,
  seguir: () => {},
  descartar: () => {},
  revisar: () => {},
};

export function useDiaEnCurso({ token, mantisBusinessId, clientCode, timezone }) {
  const [fecha, setFecha] = useState("");
  const [progreso, setProgreso] = useState(null);
  const [corriendo, setCorriendo] = useState(false);
  const [terminado, setTerminado] = useState(false);
  // The run ended in a failure: the backend's own sentence for it, for the chip.
  const [fallo, setFallo] = useState(null);
  // When the followed run was first seen, to tell its notification from older ones.
  const inicio = useRef(0);
  // Bumped each time a followed run ends: the panels reload on it.
  const [fin, setFin] = useState(0);
  // Bumped to ask again: a send, a panel mounting, the client changing.
  const [ronda, setRonda] = useState(0);
  // Whether the client processes its day from shared reports: null until asked.
  const [diario, setDiario] = useState(null);
  const esperas = useRef(0);
  const corria = useRef(false);
  const fechaRef = useRef("");
  fechaRef.current = fecha;

  const recordar = useCallback(
    (valor) => {
      if (!mantisBusinessId || !clientCode) return;
      try {
        setInStorage(claveDeFecha(mantisBusinessId, clientCode), valor || "");
      } catch {
        // Only a convenience: `runningDate` answers without it.
      }
    },
    [mantisBusinessId, clientCode]
  );

  // Another client is another day: nothing of the last one is shown.
  useEffect(() => {
    setProgreso(null);
    setCorriendo(false);
    setTerminado(false);
    setDiario(null);
    corria.current = false;
    esperas.current = 0;
    let recordada = "";
    try {
      recordada = (mantisBusinessId && clientCode && getFromStorage(claveDeFecha(mantisBusinessId, clientCode))) || "";
    } catch {
      recordada = "";
    }
    setFecha(recordada);
  }, [mantisBusinessId, clientCode]);

  useEffect(() => {
    if (!mantisBusinessId || !clientCode) return undefined;
    let activo = true;
    let espera = null;

    const consultar = async () => {
      const pedida = fechaRef.current || fechaPorDefecto(new Date(), timezone);
      const response = await getCierreDailyProgress(token, mantisBusinessId, clientCode, pedida).catch(() => null);
      if (!activo) return;
      if (!response?.success) {
        // A live run is retried; with nothing live there is nothing to say.
        if (corria.current || esperas.current > 0) espera = setTimeout(consultar, INTERVALO_AVANCE_MS);
        return;
      }
      const data = response.data || {};
      setDiario(clavesDiarias(data).length > 0);
      // Not a daily client: nothing to follow.
      if (!clavesDiarias(data).length) return;

      // Running on another day than the one asked: ask that day's counters next.
      if (data.running && data.runningDate && data.runningDate !== pedida) {
        setFecha(data.runningDate);
        fechaRef.current = data.runningDate;
        recordar(data.runningDate);
        espera = setTimeout(consultar, 0);
        return;
      }

      const vivo = Boolean(data.running);
      if (vivo || esperas.current <= 1) esperas.current = 0;
      else esperas.current -= 1;
      const siguiendo = vivo || esperas.current > 0;

      if (siguiendo || corria.current) {
        setFecha(pedida);
        // B-80 — before the run shows (the 202 lands before the lock), the counters are the day's
        // previous run's; a redo would read 100%. Nothing is counted until the run is live.
        setProgreso(vivo || !siguiendo ? data : avanceAlEnviar(data));
      }
      if (corria.current && !siguiendo) {
        // Written before the lock was let go, so it is already there when the lock is gone.
        const resultado = await resultadoDelProceso(token, clientCode, pedida, inicio.current);
        if (!activo) return;
        setFallo(resultado?.outcome === "FAILED" ? resultado.message : null);
        setTerminado(true);
        setFin((n) => n + 1);
        recordar("");
      }
      if (siguiendo && !corria.current) inicio.current = Date.now();
      if (siguiendo) {
        setTerminado(false);
        setFallo(null);
      }
      corria.current = siguiendo;
      setCorriendo(siguiendo);
      if (siguiendo) espera = setTimeout(consultar, INTERVALO_AVANCE_MS);
    };

    consultar();
    return () => {
      activo = false;
      clearTimeout(espera);
    };
  }, [token, mantisBusinessId, clientCode, timezone, ronda, recordar]);

  // A run was just sent for this date: follow it, through the moment before the lock shows.
  const seguir = useCallback(
    (fechaEnviada) => {
      if (!fechaEnviada) return;
      setFecha(fechaEnviada);
      fechaRef.current = fechaEnviada;
      recordar(fechaEnviada);
      esperas.current = ESPERAS_DE_ARRANQUE;
      inicio.current = Date.now();
      corria.current = true;
      // B-80 — not the day's previous counters: a redo read 100% before its first office.
      setProgreso(null);
      setCorriendo(true);
      setTerminado(false);
      setRonda((n) => n + 1);
    },
    [recordar]
  );

  const descartar = useCallback(() => {
    setTerminado(false);
    setFallo(null);
  }, []);

  // Ask once more — a panel mounting, so a run another person started is seen there too.
  const revisar = useCallback(() => {
    if (!corria.current) setRonda((n) => n + 1);
  }, []);

  const avance = useMemo(() => resumenDeAvance(progreso), [progreso]);
  return useMemo(
    () => ({ corriendo, terminado, fallo, fecha, avance, fin, diario, seguir, descartar, revisar }),
    [corriendo, terminado, fallo, fecha, avance, fin, diario, seguir, descartar, revisar]
  );
}
